import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import type { PhotoRecordDto } from '@forest/contracts';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { SafeMap } from '@/components/ui/SafeMap';
import { MapLayerControl } from '@/components/map/MapLayerControl';
import { ParcelVectorTilesLayer } from '@/features/parcels/ParcelVectorTilesLayer';
import { useUpdatePhoto } from './usePhotos';
import { useUiStore } from '@/stores/uiStore';
import { apiError } from '@/lib/api-client';
import { safeUrl } from '@/lib/safeUrl';
import markerIconUrl from 'leaflet/dist/images/marker-icon.png?url';
import markerIcon2xUrl from 'leaflet/dist/images/marker-icon-2x.png?url';
import markerShadowUrl from 'leaflet/dist/images/marker-shadow.png?url';

L.Icon.Default.mergeOptions({
  iconUrl: markerIconUrl,
  iconRetinaUrl: markerIcon2xUrl,
  shadowUrl: markerShadowUrl,
});

const markerIcon = L.icon({
  iconUrl: markerIconUrl,
  iconRetinaUrl: markerIcon2xUrl,
  shadowUrl: markerShadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

type Props = {
  open: boolean;
  photo: PhotoRecordDto | null;
  onClose: () => void;
  /** False shows the map read-only: no dragging, no save bar. */
  canEdit?: boolean;
};

export function PhotoViewModal({ open, photo, onClose, canEdit = true }: Props) {
  const { t } = useTranslation(['photos', 'common']);
  const pushToast = useUiStore((s) => s.pushToast);
  const updateMut = useUpdatePhoto();

  const origin = useMemo<[number, number] | null>(
    () => (photo ? [photo.latitude, photo.longitude] : null),
    [photo],
  );

  const [draft, setDraft] = useState<[number, number] | null>(origin);
  const markerRef = useRef<L.Marker | null>(null);

  useEffect(() => {
    setDraft(origin);
  }, [origin]);

  if (!photo || !draft || !origin) return null;

  const dirty = draft[0] !== origin[0] || draft[1] !== origin[1];

  const onSave = async () => {
    try {
      await updateMut.mutateAsync({
        id: photo.id,
        input: { latitude: draft[0], longitude: draft[1] },
      });
      pushToast({
        variant: 'success',
        message: t('photos:modal.coordsSaved', { defaultValue: 'Coordonate salvate' }),
      });
      onClose();
    } catch (e) {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    }
  };

  const onReset = () => setDraft(origin);

  const title = t('photos:modal.title', { fileName: photo.fileName });

  return (
    <Dialog open={open} onClose={onClose} title={title} size="xl">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3 text-xs text-slate-600 sm:grid-cols-3">
          <Info label={t('photos:modal.info.fileName')} value={photo.fileName} />
          {/* The polygon, not its UUID: a raw id told the user nothing, and
              the photo's own location columns are nulled once it is linked. */}
          <Info
            label={t('photos:modal.info.parcel')}
            value={
              photo.parcel
                ? `${photo.parcel.codProiect ?? '—'} · UCF ${photo.parcel.ucf}`
                : t('photos:modal.info.noParcel')
            }
          />
          <Info label={t('photos:table.header.ucf')} value={photo.parcel?.ucf ?? photo.ucf ?? '—'} />
          <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5">
            <div className="text-[10px] uppercase text-slate-500">
              {t('photos:modal.info.downloadUrl')}
            </div>
            <a
              href={safeUrl(photo.downloadUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-forest-700 underline hover:text-forest-800"
            >
              {t('photos:modal.downloadLink')}
            </a>
          </div>
        </div>

        {photo.parcel ? (
          <div className="rounded border border-forest-200 bg-forest-50/60 px-3 py-2 dark:border-forest-900 dark:bg-forest-950/30">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-forest-800 dark:text-forest-300">
              {t('photos:modal.polygonData')}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
              <Pair label={t('photos:table.header.ucf')} value={photo.parcel.ucf} />
              <Pair
                label={t('photos:preview.codProiect')}
                value={photo.parcel.codProiect ?? '—'}
              />
              <Pair
                label={t('photos:filters.projectIdPlaceholder', {
                  defaultValue: 'ID proiect',
                })}
                value={photo.parcel.projectId ?? '—'}
              />
              <Pair label={t('photos:preview.raion')} value={photo.parcel.raion} />
              <Pair label={t('photos:preview.primarie')} value={photo.parcel.primarie} />
              <Pair
                label={t('photos:preview.ocolSilvic')}
                value={photo.parcel.ocolSilvic ?? '—'}
              />
              <Pair
                label={t('photos:preview.entitateSilvica')}
                value={photo.parcel.entitateSilvica ?? '—'}
              />
              <Pair
                label={t('photos:preview.suprafata')}
                value={`${photo.parcel.suprafata.toFixed(2)} ha`}
              />
              <Pair label={t('photos:preview.anul')} value={String(photo.parcel.anul)} />
              {photo.subParcel ? (
                <Pair
                  label={t('photos:preview.subParcel')}
                  value={`${photo.subParcel.ucf} · ${photo.subParcel.suprafata.toFixed(2)} ha`}
                />
              ) : null}
            </div>
          </div>
        ) : null}

        {canEdit ? (
          <div
            className={[
              'flex flex-wrap items-center justify-between gap-2 rounded border px-3 py-2 text-xs',
              dirty
                ? 'border-amber-300 bg-amber-50 text-amber-900'
                : 'border-slate-200 bg-slate-50 text-slate-600',
            ].join(' ')}
          >
            <div>
              <span className="font-semibold">
                {t('photos:modal.editHint', {
                  defaultValue: 'Trageți marcatorul pentru a muta fotografia.',
                })}
              </span>{' '}
              <span className="font-mono">
                {draft[0].toFixed(6)}, {draft[1].toFixed(6)}
              </span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={onReset}
                disabled={!dirty || updateMut.isPending}
              >
                {t('common:action.reset', { defaultValue: 'Resetează' })}
              </Button>
              <Button onClick={onSave} disabled={!dirty} loading={updateMut.isPending}>
                {t('common:action.save', { defaultValue: 'Salvează' })}
              </Button>
            </div>
          </div>
        ) : null}

        <SafeMap height={480} className="overflow-hidden rounded border border-slate-200">
          <MapContainer
            center={origin}
            zoom={15}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom
          >
            <MapLayerControl />
            {/* Server-rendered MVT tiles, the same layer /app/filter uses.
                The previous `useParcels({ limit: 500 })` + <GeoJSON> pair
                could only ever draw the newest 500 of ~3,550 polygons, so
                the parcel under the photo was usually absent — exactly the
                context this map exists to provide. Tiles are
                viewport-paginated, so all polygons render and the modal
                stops pulling 500 full geometries. */}
            <ParcelVectorTilesLayer enabled />
            <Marker
              position={draft}
              icon={markerIcon}
              draggable={canEdit}
              eventHandlers={{
                dragend: () => {
                  const m = markerRef.current;
                  if (!m) return;
                  const ll = m.getLatLng();
                  setDraft([ll.lat, ll.lng]);
                },
              }}
              ref={(instance) => {
                markerRef.current = instance ?? null;
              }}
            >
              <Popup>
                <div style={{ fontSize: '12px', lineHeight: 1.4 }}>
                  <div>
                    <strong>{photo.fileName}</strong>
                  </div>
                  {photo.parcel ? (
                    <div>
                      UCF {photo.parcel.ucf} · {photo.parcel.raion}
                    </div>
                  ) : photo.raion ? (
                    <div>{photo.raion}</div>
                  ) : null}
                  <div>
                    {draft[0].toFixed(6)}, {draft[1].toFixed(6)}
                  </div>
                  <a
                    href={safeUrl(photo.downloadUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('photos:modal.downloadLink')}
                  </a>
                </div>
              </Popup>
            </Marker>
          </MapContainer>
        </SafeMap>
      </div>
    </Dialog>
  );
}

/** Compact label/value pair inside the polygon block. */
function Pair({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className="truncate text-slate-800 dark:text-slate-200" title={value}>
        {value}
      </div>
    </div>
  );
}

function Info({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) {
  return (
    <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5">
      <div className="text-[10px] uppercase text-slate-500">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}
